import axios from "axios";
import { storage, STORAGE_KEYS } from "./storage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach token from localStorage as Authorization header too (extra-robust across cookie/SameSite quirks).
// Skipped when the caller already set an Authorization header (e.g. preauth token during 2FA login).
api.interceptors.request.use((config) => {
  const token = storage.get(STORAGE_KEYS.TOKEN);
  if (token && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function formatApiError(detail) {
  if (detail == null) return "Terjadi kesalahan. Silakan coba lagi.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Step 1 of login: verify password (login pakai NIP). Returns { preauthToken,
// totpEnrolled } — the real access token is only issued after TOTP verification.
export async function loginRequest(nip, password) {
  const { data } = await api.post("/auth/login", { nip, password });
  return data;
}

function preauthHeaders(preauthToken) {
  return preauthToken ? { Authorization: `Bearer ${preauthToken}` } : {};
}

function storeSession(data) {
  if (data?.token) storage.set(STORAGE_KEYS.TOKEN, data.token);
  if (data?.user) storage.set(STORAGE_KEYS.USER, data.user);
  return data.user;
}

// Generate/regenerate TOTP secret + QR. Works with preauth token (during login)
// or with the normal access token (re-enroll from Pengaturan) when preauthToken is null.
export async function twoFaSetup(preauthToken = null) {
  const { data } = await api.post("/auth/2fa/setup", {}, { headers: preauthHeaders(preauthToken) });
  return data; // { secret, otpauthUri, qrPngBase64 }
}

// Confirm enrollment with the first code. During login (preauth) this also
// returns the access token + user, which we store like a normal login.
export async function twoFaActivate(preauthToken, code) {
  const { data } = await api.post("/auth/2fa/activate", { code }, { headers: preauthHeaders(preauthToken) });
  return storeSession(data);
}

// Step 2 of login: verify the 6-digit code, store session.
export async function twoFaVerify(preauthToken, code) {
  const { data } = await api.post("/auth/2fa/verify", { code }, { headers: preauthHeaders(preauthToken) });
  return storeSession(data);
}

export async function changePasswordRequest(currentPassword, newPassword) {
  const { data } = await api.post("/auth/change-password", { currentPassword, newPassword });
  return data;
}

export async function fetchMe() {
  const { data } = await api.get("/auth/me");
  storage.set(STORAGE_KEYS.USER, data);
  return data;
}

export async function logoutRequest() {
  try {
    await api.post("/auth/logout");
  } catch {
    /* ignore network errors on logout */
  } finally {
    storage.remove(STORAGE_KEYS.TOKEN);
    storage.remove(STORAGE_KEYS.USER);
  }
}

export function getCachedUser() {
  return storage.get(STORAGE_KEYS.USER, null);
}

// Hanya IRBAN dan ADMIN yang memegang semua peran sekaligus — otoritas penuh,
// dan boleh melakukan apa pun yang bisa dilakukan Dalnis/Ketua Tim/Anggota Tim.
// Ini satu-satunya tempat aturan itu didefinisikan; jangan bandingkan string
// role secara langsung di komponen.
export const PERAN_SEMUA_AKSES = ["IRBAN", "ADMIN"];

export function isAdmin(user) {
  return user?.role === "ADMIN";
}

export function isSuperRole(user) {
  return PERAN_SEMUA_AKSES.includes(user?.role);
}

// Catatan: hasRole hanya untuk mengecek KEWENANGAN ("boleh tidak?").
// Untuk mengecek KETERBATASAN ("apakah user ini terbatas?") jangan pakai ini —
// IRBAN/ADMIN akan selalu lolos dan hasilnya jadi terbalik.
export function hasRole(user, ...roles) {
  if (!user) return false;
  if (isSuperRole(user)) return true;
  return roles.includes(user.role);
}
